import React, { useEffect, useState } from "react";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import CarCard from "./CarCard";
import { useLang } from "../contexts/LanguageContext";
import { ArrowRight, Loader2, Sparkles, TrendingDown, TrendingUp } from "lucide-react";

// Locale-translated similarity strings.
const SIM_I18N = {
  de: {
    section: "Ähnlich",
    heading: "Ähnliche Fahrzeuge",
    viewAll: "Alle ansehen",
    loading: "Ähnliche Fahrzeuge werden geladen …",
    fewMatches: "Zu wenige direkte Treffer",
    moreByBrand: (b) => `Mehr Fahrzeuge von ${b}`,
    moreByBody: (b) => `Mehr Fahrzeuge in der Kategorie ${b}`,
    moreDiscover: "Mehr Fahrzeuge entdecken",
    labels: {
      very_similar: "Sehr ähnlich",
      top_match: "Top-Match",
      alternative: "Alternative",
    },
    variants: {
      cheaper_alternative: "Preiswerter",
      premium_alternative: "Premium",
    },
  },
  fr: {
    section: "Similaire",
    heading: "Véhicules similaires",
    viewAll: "Voir tout",
    loading: "Chargement des véhicules similaires…",
    fewMatches: "Peu de correspondances directes",
    moreByBrand: (b) => `Plus de véhicules ${b}`,
    moreByBody: (b) => `Plus de véhicules ${b}`,
    moreDiscover: "Découvrir plus de véhicules",
    labels: {
      very_similar: "Très similaire",
      top_match: "Top match",
      alternative: "Alternative",
    },
    variants: {
      cheaper_alternative: "Moins cher",
      premium_alternative: "Premium",
    },
  },
  it: {
    section: "Simile",
    heading: "Veicoli simili",
    viewAll: "Vedi tutti",
    loading: "Caricamento veicoli simili…",
    fewMatches: "Poche corrispondenze dirette",
    moreByBrand: (b) => `Altri veicoli ${b}`,
    moreByBody: (b) => `Altri veicoli ${b}`,
    moreDiscover: "Scopri altri veicoli",
    labels: {
      very_similar: "Molto simile",
      top_match: "Top match",
      alternative: "Alternativa",
    },
    variants: {
      cheaper_alternative: "Più economico",
      premium_alternative: "Premium",
    },
  },
  en: {
    section: "Similar",
    heading: "Similar vehicles",
    viewAll: "View all",
    loading: "Loading similar vehicles…",
    fewMatches: "Few direct matches",
    moreByBrand: (b) => `More ${b} vehicles`,
    moreByBody: (b) => `More ${b} vehicles`,
    moreDiscover: "Discover more vehicles",
    labels: {
      very_similar: "Very similar",
      top_match: "Top match",
      alternative: "Alternative",
    },
    variants: {
      cheaper_alternative: "Cheaper",
      premium_alternative: "Premium",
    },
  },
};

/**
 * Similarity pill — small, premium, dezent.
 *  very_similar → emerald
 *  top_match    → red (brand-accent)
 *  alternative  → zinc / muted
 */
function SimilarityBadge({ label, t }) {
  const map = {
    very_similar: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
    top_match: "border-red-500/40 bg-red-600/15 text-red-300",
    alternative: "border-zinc-700/70 bg-zinc-900/80 text-zinc-300",
  };
  const cls = map[label];
  if (!cls) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-md ${cls}`}
      data-testid={`similarity-badge-${label}`}
    >
      <Sparkles className="h-3 w-3" />
      {t.labels[label]}
    </span>
  );
}

function VariantBadge({ variant, t }) {
  if (!variant) return null;
  const isCheaper = variant === "cheaper_alternative";
  const Icon = isCheaper ? TrendingDown : TrendingUp;
  const cls = isCheaper
    ? "border-sky-500/40 bg-sky-500/15 text-sky-300"
    : "border-amber-500/40 bg-amber-500/15 text-amber-200";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-md ${cls}`}
      data-testid={`similarity-variant-${variant}`}
    >
      <Icon className="h-3 w-3" />
      {t.variants[variant]}
    </span>
  );
}

function StackedBadges({ car, t }) {
  if (!car.similarity_label && !car.similarity_variant) return null;
  return (
    <div className="flex flex-col gap-1" data-testid={`similarity-stack-${car.id}`}>
      <SimilarityBadge label={car.similarity_label} t={t} />
      <VariantBadge variant={car.similarity_variant} t={t} />
    </div>
  );
}

/**
 * Horizontal-scroll on mobile, 3-column grid on desktop.
 * Fetches /api/listings/{id}/similar; renders nothing if empty + no fallback.
 */
export default function SimilarListings({ listingId, sourceBrand, sourceBody }) {
  const [items, setItems] = useState([]);
  const [fallback, setFallback] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang } = useLang();
  const t = SIM_I18N[lang] || SIM_I18N.de;

  useEffect(() => {
    if (!listingId) return;
    setLoading(true);
    api.getSimilarListings(listingId)
      .then((r) => {
        setItems(Array.isArray(r?.items) ? r.items : []);
        setFallback(r?.fallback || null);
      })
      .catch(() => { setItems([]); setFallback(null); })
      .finally(() => setLoading(false));
  }, [listingId]);

  if (loading) {
    return (
      <div className="mt-12 flex items-center gap-2 text-sm text-zinc-500" data-testid="similar-listings-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> {t.loading}
      </div>
    );
  }
  if (items.length === 0 && !fallback) return null;

  const fallbackParams = fallback?.params || {};
  const fallbackQs = new URLSearchParams(fallbackParams).toString();
  const fallbackLabel = fallbackParams.body
    ? t.moreByBody(fallbackParams.body)
    : fallbackParams.brand
      ? t.moreByBrand(fallbackParams.brand)
      : t.moreDiscover;

  return (
    <section className="mt-14" data-testid="similar-listings-section">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">{t.section}</p>
          <h2 className="mt-1 font-display text-xl font-semibold text-white sm:text-2xl">{t.heading}</h2>
        </div>
        <Link
          to={`/listings${sourceBrand || sourceBody ? "?" + new URLSearchParams({ ...(sourceBrand ? { brand: sourceBrand } : {}), ...(sourceBody ? { body: sourceBody } : {}) }).toString() : ""}`}
          className="hidden text-xs text-zinc-400 hover:text-red-400 sm:inline-flex sm:items-center sm:gap-1"
          data-testid="similar-listings-view-all"
        >
          {t.viewAll} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {items.length > 0 && (
        <>
          {/* Mobile: horizontal scroll snap */}
          <div className="mt-6 -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-3 sm:hidden" data-testid="similar-listings-mobile">
            {items.map((it) => (
              <div key={it.id} className="w-[78%] shrink-0 snap-start">
                <CarCard car={it} similarityBadge={<StackedBadges car={it} t={t} />} />
              </div>
            ))}
          </div>

          {/* Desktop / tablet: grid */}
          <div className="mt-6 hidden gap-4 sm:grid sm:grid-cols-2 lg:grid-cols-3" data-testid="similar-listings-grid">
            {items.slice(0, 6).map((it) => (
              <CarCard key={it.id} car={it} similarityBadge={<StackedBadges car={it} t={t} />} />
            ))}
          </div>
        </>
      )}

      {fallback && (
        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-4 text-center text-sm text-zinc-300 sm:p-5" data-testid="similar-listings-fallback">
          <p className="text-xs text-zinc-500">{t.fewMatches}</p>
          <Link
            to={`/listings${fallbackQs ? "?" + fallbackQs : ""}`}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-red-400 hover:text-red-300"
            data-testid="similar-listings-fallback-link"
          >
            {fallbackLabel} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </section>
  );
}
